import { NAVIGATION_PAGE } from "../redux/constants/metaData";
import {
  DOCUMENT_PANEL_ALL_DOCS_INFO_CHANNEL,
  DOCUMENT_PANEL_ALL_DOCS_INFO_GENERAL,
  DOCUMENT_PANEL_CHECKIN_NEW_INFO_CHANNEL,
  DOCUMENT_PANEL_CHECKIN_NEW_INFO_GENERAL,
  DOCUMENT_PANEL_TEMPLATES_INFO,
  DOCUMENT_PANEL_WORKING_DOCS_INFO_CHANNEL,
  DOCUMENT_PANEL_WORKING_DOCS_INFO_GENERAL,
} from "../redux/constants/messageTypes";

/** Route path constants (derived from APP_NAV_ITEMS). */
/** Router location.state keys (e.g. skip list refetch after cancel). */
export const NAV_LOCATION_STATE = {
  skipWorkingDocumentsRefresh: "skipWorkingDocumentsRefresh",
  /** After check-out from All Documents: force one Working Documents list fetch on mount. */
  forceRefreshWorkingDocuments: "forceRefreshWorkingDocuments",
  /** Re-click sidebar Templates or accordion header: refetch template list. */
  forceRefreshTemplates: "forceRefreshTemplates",
  /** Accordion expand-arrow navigation: skip route-change list refresh. */
  skipDocumentPanelRouteRefresh: "skipDocumentPanelRouteRefresh",
  /** Restore OneDrive Documents folder breadcrumb after Preview in Teams closes. */
  restoreCheckInNewFolder: "restoreCheckInNewFolder",
};

export const APP_ROUTES = {
  root: "/",
  workingDocuments: "/working-documents",
  workingDocumentCheckIn: "/working-documents/check-in",
  allDocuments: "/all-documents",
  templates: "/templates",
  checkIn: "/check-in",
  checkInNew: "/check-in-new",
  checkInNewForm: "/check-in-new/form",
  checkInNewRevision: "/check-in-new-revision",
  checkInNewList: "/check-in-new-list",
  /** Multi-document check-in scaffold (expanded rows per file). */
  checkInNewBulk: "/check-in-new/bulk-check-in",
};

/**
 * Single source of truth for main app navigation: label, key, route, and icon key.
 * Use this to build sidebar links, route tables, and path→key lookups.
 */
export const APP_NAV_ITEMS = [
  {
    key: NAVIGATION_PAGE.home,
    label: "Working Documents",
    route: APP_ROUTES.workingDocuments,
    iconKey: "workingDocuments",
  },
  {
    key: NAVIGATION_PAGE.allDocuments,
    label: "All Documents",
    route: APP_ROUTES.allDocuments,
    iconKey: "allDocuments",
  },
  {
    key: NAVIGATION_PAGE.templates,
    label: "Templates",
    route: APP_ROUTES.templates,
    iconKey: "templates",
  },
  {
    key: NAVIGATION_PAGE.checkinFromTeams,
    /** Fallback only; sidebar/accordion use getCheckInDocumentsNavLabel(teamsContext). */
    label: "OneDrive Documents",
    route: APP_ROUTES.checkInNew,
    iconKey: "checkInNew",
  },
];

/** Sidebar / accordion title for the check-in document list (general vs channel app). */
export const CHECK_IN_DOCUMENTS_NAV_LABEL = {
  general: "OneDrive Documents",
  channel: "Channel Documents",
};

export function getCheckInDocumentsNavLabel(teamsContext) {
  const isGeneralAppContext = !teamsContext?.channel?.id;
  return isGeneralAppContext
    ? CHECK_IN_DOCUMENTS_NAV_LABEL.general
    : CHECK_IN_DOCUMENTS_NAV_LABEL.channel;
}

/** Map nav key → route path. */
export const NAV_ROUTE_BY_KEY = Object.fromEntries(
  APP_NAV_ITEMS.map((item) => [item.key, item.route])
);

/** Map route path → nav key. Check-in routes use the same sidebar check-in documents item. */
export const ROUTE_NAV_KEY_BY_PATH = Object.fromEntries([
  ...APP_NAV_ITEMS.map((item) => [item.route, item.key]),
  [APP_ROUTES.checkIn, NAVIGATION_PAGE.checkinFromTeams],
  [APP_ROUTES.checkInNewForm, NAVIGATION_PAGE.checkinFromTeams],
  [APP_ROUTES.checkInNewRevision, NAVIGATION_PAGE.checkinFromTeams],
  [APP_ROUTES.checkInNewBulk, NAVIGATION_PAGE.checkinFromTeams],
  // Working-doc check-in hub stays under Working Documents in the sidebar.
  [APP_ROUTES.workingDocumentCheckIn, NAVIGATION_PAGE.home],
]);

export function getNavKeyFromPath(pathname, locationState) {
  // Bulk check-in is reachable from both OneDrive Documents and Working
  // Documents; keep the sidebar highlighted on the originating tab.
  if (
    pathname === APP_ROUTES.checkInNewBulk &&
    locationState?.from === APP_ROUTES.workingDocuments
  ) {
    return NAVIGATION_PAGE.home;
  }
  return ROUTE_NAV_KEY_BY_PATH[pathname] ?? NAVIGATION_PAGE.home;
}

/** Get route for a nav key. */
export function getRouteByNavKey(navKey) {
  return NAV_ROUTE_BY_KEY[navKey] ?? APP_ROUTES.workingDocuments;
}

/** Get the default (first) route for redirects. */
export function getDefaultRoute() {
  return APP_ROUTES.workingDocuments;
}

/** Document-tabs accordion panel ids (use these instead of "panel1" / "panel2"). */
export const DOCUMENT_TAB_PANEL = {
  workingDocuments: "workingDocumentPanel",
  allDocuments: "allDocumentPanel",
  templates: "templatesPanel",
  checkInNew: "checkInNewPanel",
};

/** Accordion header info tooltips (general vs channel app). */
export const DOCUMENT_PANEL_INFO_TOOLTIP = {
  general: {
    [DOCUMENT_TAB_PANEL.workingDocuments]: DOCUMENT_PANEL_WORKING_DOCS_INFO_GENERAL,
    [DOCUMENT_TAB_PANEL.allDocuments]: DOCUMENT_PANEL_ALL_DOCS_INFO_GENERAL,
    [DOCUMENT_TAB_PANEL.templates]: DOCUMENT_PANEL_TEMPLATES_INFO,
    [DOCUMENT_TAB_PANEL.checkInNew]: DOCUMENT_PANEL_CHECKIN_NEW_INFO_GENERAL,
  },
  channel: {
    [DOCUMENT_TAB_PANEL.workingDocuments]: DOCUMENT_PANEL_WORKING_DOCS_INFO_CHANNEL,
    [DOCUMENT_TAB_PANEL.allDocuments]: DOCUMENT_PANEL_ALL_DOCS_INFO_CHANNEL,
    [DOCUMENT_TAB_PANEL.templates]: DOCUMENT_PANEL_TEMPLATES_INFO,
    [DOCUMENT_TAB_PANEL.checkInNew]: DOCUMENT_PANEL_CHECKIN_NEW_INFO_CHANNEL,
  },
};

export function getDocumentPanelInfoTooltip(teamsContext, panelId) {
  const isGeneralAppContext = !teamsContext?.channel?.id;
  const scope = isGeneralAppContext ? "general" : "channel";
  return DOCUMENT_PANEL_INFO_TOOLTIP[scope][panelId] ?? "";
}

/** Document-tab panel id → route. */
export const DOCUMENT_TAB_PANEL_ROUTE = {
  [DOCUMENT_TAB_PANEL.workingDocuments]: APP_ROUTES.workingDocuments,
  [DOCUMENT_TAB_PANEL.allDocuments]: APP_ROUTES.allDocuments,
  [DOCUMENT_TAB_PANEL.templates]: APP_ROUTES.templates,
  [DOCUMENT_TAB_PANEL.checkInNew]: APP_ROUTES.checkInNew,
};

/** Pathname → document-tab panel id, or null if not a document-tab route. */
export function getDocumentPanelFromPath(pathname) {
  if (pathname === APP_ROUTES.workingDocuments) return DOCUMENT_TAB_PANEL.workingDocuments;
  if (pathname === APP_ROUTES.allDocuments) return DOCUMENT_TAB_PANEL.allDocuments;
  if (pathname === APP_ROUTES.templates) return DOCUMENT_TAB_PANEL.templates;
  if (pathname === APP_ROUTES.checkInNew) return DOCUMENT_TAB_PANEL.checkInNew;
  return null;
}

/** Get route for document-tab panel id. */
export function getRouteByDocumentPanel(panelId) {
  return DOCUMENT_TAB_PANEL_ROUTE[panelId] ?? APP_ROUTES.workingDocuments;
}

/** Get nav key for document-tab panel id (for onActivePageChange). */
export function getNavKeyByDocumentPanel(panelId) {
  const route = getRouteByDocumentPanel(panelId);
  return ROUTE_NAV_KEY_BY_PATH[route] ?? NAVIGATION_PAGE.home;
}
